/**
 * @fileoverview Case Ingestion Assist Types - Types for the CMS import assist system.
 *
 * Defines the import artifact contract, imported field presence semantics,
 * and step-shaped payload types aligned with UNIFIED_STEPS.
 *
 * Presence semantics:
 * - `present`: Value was found in the source CMS and imported.
 * - `missing`: Value was expected but not found in the source CMS.
 * - `not_applicable`: Field does not apply to this case type in the source CMS.
 */
import { z } from 'zod';

// =============================================================================
// IMPORTED FIELD PRESENCE
// =============================================================================

export const IMPORTED_FIELD_PRESENCE = ['present', 'missing', 'not_applicable'] as const;
export type ImportedFieldPresence = (typeof IMPORTED_FIELD_PRESENCE)[number];

// =============================================================================
// IMPORTED FIELD WRAPPER
// =============================================================================

/**
 * Wraps an imported value with presence metadata.
 * Every field in the import payload uses this shape.
 */
export interface ImportedField<T = string> {
  value: T | null;
  presence: ImportedFieldPresence;
}

// =============================================================================
// STEP-SHAPED IMPORT PAYLOADS
// =============================================================================

/** Case Info step imported fields */
export interface ImportedCaseInfo {
  firm_name: ImportedField;
  representing_attorney: ImportedField;
  case_manager: ImportedField;
  date_of_loss: ImportedField;
  statute_of_limitations_date: ImportedField;
  incident_location: ImportedField;
  incident_summary: ImportedField;
  case_type: ImportedField;
  case_id: ImportedField;
  external_cms_url: ImportedField;
  /** URLs of CMS pages this section was sourced from */
  source_urls?: string[];
}

/** Client Info step imported fields */
export interface ImportedClientInfo {
  first_name: ImportedField;
  last_name: ImportedField;
  email: ImportedField;
  phone_number: ImportedField;
  date_of_birth: ImportedField;
  ssn: ImportedField;
  ssn_last_four: ImportedField;
  gender: ImportedField;
  preferred_language: ImportedField;
  address: ImportedField<ImportedAddress>;
  /** URLs of CMS pages this section was sourced from */
  source_urls?: string[];
}

/** Structured address imported from CMS */
export interface ImportedAddress {
  line1: string;
  line2: string | null;
  city: string | null;
  state: string | null;
  postcode: string | null;
}

/** Liability step imported fields */
export interface ImportedLiability {
  police_report_number: ImportedField;
  police_report_date: ImportedField;
  police_department: ImportedField;
  at_fault_party: ImportedField;
  liability_notes: ImportedField;
  /** URLs of CMS pages this section was sourced from */
  source_urls?: string[];
}

/** Treatment step imported fields */
export interface ImportedTreatment {
  providers: ImportedField<ImportedProvider[]>;
  treatment_notes: ImportedField;
  /** URLs of CMS pages this section was sourced from */
  source_urls?: string[];
}

/** A single imported medical provider */
export interface ImportedProvider {
  name: string;
  type: string | null;
  phone: string | null;
  fax: string | null;
  address_line1: string | null;
  address_line2: string | null;
  city: string | null;
  state: string | null;
  postcode: string | null;
  treatment_status: string | null;
  /** Care level: EMERGENCY_CARE, CONSERVATIVE_CARE, etc. */
  care_level: string | null;
  /** Facility type: hospital or clinic */
  facility_type: string | null;
}

/** Coverage step imported fields */
export interface ImportedCoverage {
  first_party: ImportedField<ImportedCoverageEntry[]>;
  third_party: ImportedField<ImportedCoverageEntry[]>;
  health_insurance: ImportedField<ImportedCoverageEntry[]>;
  coverage_notes: ImportedField;
  /** URLs of CMS pages this section was sourced from */
  source_urls?: string[];
}

/** A single imported coverage entry */
export interface ImportedCoverageEntry {
  carrier_name: string;
  policy_number: string | null;
  claim_number: string | null;
  named_insured: string | null;
  policy_type: string | null;
  adjuster_name: string | null;
  adjuster_phone: string | null;
  adjuster_email: string | null;
  adjuster_fax: string | null;
  policy_limit: string | null;
}

/** Companion cases imported fields */
export interface ImportedCompanionCases {
  /** Whether the CMS indicates this case has companions/related cases */
  has_companions: ImportedField;
  /** Names or IDs of related cases found in the CMS */
  related_case_names: ImportedField<string[]>;
  /** URLs of CMS pages this section was sourced from */
  source_urls?: string[];
}

/** Top-level import payload aligned with UNIFIED_STEPS */
export interface ImportPayload {
  case_info: ImportedCaseInfo;
  client_info: ImportedClientInfo;
  companion_cases: ImportedCompanionCases;
  liability: ImportedLiability;
  treatment: ImportedTreatment;
  coverage: ImportedCoverage;
  /** Free-text notes from the CMS that don't map to structured fields */
  source_notes: string | null;
}

// =============================================================================
// IMPORT ARTIFACT (API response shape)
// =============================================================================

/**
 * The persisted import artifact stored on the case-ingestion row.
 * One per matter in v1.
 *
 * Note: No `id` field — the import lives on the CaseIngestionResult row,
 * not a separate table. The backend returns `matter_id` as the identifier.
 */
export interface CaseIngestionImport {
  matter_id: string;
  imported_at: string;
  payload: ImportPayload;
}

// =============================================================================
// REQUEST / RESPONSE TYPES
// =============================================================================

/** Request body for creating an import — just the payload */
export interface CreateCaseIngestionImportRequest {
  payload: ImportPayload;
}

// =============================================================================
// ZOD SCHEMAS (for frontend validation of pasted JSON)
// =============================================================================

const importedFieldPresenceSchema = z.enum(IMPORTED_FIELD_PRESENCE);

function createImportedFieldSchema<T extends z.ZodTypeAny>(valueSchema: T) {
  return z.object({
    value: valueSchema.nullable(),
    presence: importedFieldPresenceSchema,
  });
}

const ImportedFieldSchema = createImportedFieldSchema(z.string());

const ImportedAddressSchema = z.object({
  line1: z.string(),
  line2: z.string().nullable(),
  city: z.string().nullable(),
  state: z.string().nullable(),
  postcode: z.string().nullable(),
});

const ImportedFieldWithAddressSchema = createImportedFieldSchema(
  ImportedAddressSchema,
);

const ImportedProviderSchema = z.object({
  name: z.string(),
  type: z.string().nullable(),
  phone: z.string().nullable(),
  fax: z.string().nullable(),
  address_line1: z.string().nullable(),
  address_line2: z.string().nullable(),
  city: z.string().nullable(),
  state: z.string().nullable(),
  postcode: z.string().nullable(),
  treatment_status: z.string().nullable(),
  care_level: z.string().nullable(),
  facility_type: z.string().nullable(),
});

const ImportedCoverageEntrySchema = z.object({
  carrier_name: z.string(),
  policy_number: z.string().nullable(),
  claim_number: z.string().nullable(),
  named_insured: z.string().nullable(),
  policy_type: z.string().nullable(),
  adjuster_name: z.string().nullable(),
  adjuster_phone: z.string().nullable(),
  adjuster_email: z.string().nullable(),
  adjuster_fax: z.string().nullable(),
  policy_limit: z.string().nullable(),
});

const ImportedFieldWithProvidersSchema = createImportedFieldSchema(
  z.array(ImportedProviderSchema),
);

const ImportedFieldWithCoverageSchema = createImportedFieldSchema(
  z.array(ImportedCoverageEntrySchema),
);

export const ImportPayloadSchema = z.object({
  case_info: z.object({
    firm_name: ImportedFieldSchema,
    representing_attorney: ImportedFieldSchema,
    case_manager: ImportedFieldSchema,
    date_of_loss: ImportedFieldSchema,
    statute_of_limitations_date: ImportedFieldSchema,
    incident_location: ImportedFieldSchema,
    incident_summary: ImportedFieldSchema,
    case_type: ImportedFieldSchema,
    case_id: ImportedFieldSchema,
    external_cms_url: ImportedFieldSchema,
  }),
  client_info: z.object({
    first_name: ImportedFieldSchema,
    last_name: ImportedFieldSchema,
    email: ImportedFieldSchema,
    phone_number: ImportedFieldSchema,
    date_of_birth: ImportedFieldSchema,
    ssn: ImportedFieldSchema,
    ssn_last_four: ImportedFieldSchema,
    gender: ImportedFieldSchema,
    preferred_language: ImportedFieldSchema,
    address: ImportedFieldWithAddressSchema,
  }),
  companion_cases: z.object({
    has_companions: ImportedFieldSchema,
    related_case_names: createImportedFieldSchema(z.array(z.string())),
  }),
  liability: z.object({
    police_report_number: ImportedFieldSchema,
    police_report_date: ImportedFieldSchema,
    police_department: ImportedFieldSchema,
    at_fault_party: ImportedFieldSchema,
    liability_notes: ImportedFieldSchema,
  }),
  treatment: z.object({
    providers: ImportedFieldWithProvidersSchema,
    treatment_notes: ImportedFieldSchema,
  }),
  coverage: z.object({
    first_party: ImportedFieldWithCoverageSchema,
    third_party: ImportedFieldWithCoverageSchema,
    health_insurance: ImportedFieldWithCoverageSchema,
    coverage_notes: ImportedFieldSchema,
  }),
  source_notes: z.string().nullable(),
});

export const CreateImportRequestSchema = z.object({
  payload: ImportPayloadSchema,
});
